import { createRng } from './rng';
import { generateLayeredNoiseHeights, generateMazeHeights, generateZigguratHeights } from './terrain';
import type { Level } from './types';

export const defaultSize = 8;

/**
 * `'uniform'` is the original independent-per-tile random generation
 * (isolated single-tile spikes/dips). `'noise'` blends coarse and fine
 * value-noise layers for softer, connected rolling terrain. `'ziggurat'`
 * and `'maze'` are fully structured, non-random patterns (see terrain.ts).
 */
export type TerrainPattern = 'uniform' | 'noise' | 'ziggurat' | 'maze';

export type GenerateLevelOptions = {
  pattern?: TerrainPattern;
  /** Overrides the default "Seed X" display name. */
  name?: string;
  /** Controls the coarse/fine noise blend for 'noise' pattern (0–1, default 0.7). */
  smoothness?: number;
  /** Center of the height generation range. Default: `size / 4`. */
  heightCenter?: number;
  /** Half-width of the generation range around center. Default: `size / 8`. */
  heightSpread?: number;
};

export function generateLevel(
  seed: number | string,
  size: number = defaultSize,
  options: GenerateLevelOptions = {},
): Level {
  const { pattern = 'uniform', name, smoothness, heightCenter, heightSpread } = options;
  const rng = createRng(seed);

  const hardLimit = size / 2;
  const center = heightCenter ?? hardLimit / 2;
  const spread = heightSpread ?? hardLimit / 4;

  const genFloor = center - spread;
  const genCeiling = center + spread;

  if (genFloor < 0 || genCeiling > hardLimit) {
    throw new Error(
      `Invalid generation range: heightCenter (${center}) ± heightSpread (${spread}) ` +
      `produces [${genFloor}, ${genCeiling}] which exceeds boundaries [0, ${hardLimit}]`,
    );
  }

  let heights: number[];
  let actualSize = size;

  switch (pattern) {
    case 'noise':
      heights = generateLayeredNoiseHeights(rng, size, genFloor, genCeiling, smoothness);
      break;
    case 'ziggurat':
      heights = generateZigguratHeights(size, genFloor, genCeiling);
      break;
    case 'maze': {
      const mazeCells = Math.max(1, Math.floor((size - 1) / 2));
      const maze = generateMazeHeights(rng, mazeCells, genFloor, genCeiling);
      heights = maze.heights;
      actualSize = maze.size;
      break;
    }
    case 'uniform':
    default:
      heights = [];
      for (let index = 0; index < size * size; index += 1) {
        heights.push(genFloor + rng.next() * (genCeiling - genFloor));
      }
      break;
  }

  return {
    id: `seed-${seed}`,
    name: name ?? `Seed ${seed}`,
    size: actualSize,
    heightCenter: center,
    heightSpread: spread,
    heights,
  };
}

export const baseLevels: Level[] = [
  generateLevel('intro', 7, { name: 'Intro' }),
  generateLevel('grass', 8, { name: 'Grass' }),
  generateLevel('foothills', 10, { pattern: 'noise', name: 'Foothills' }),
  generateLevel('ziggurat', 9, { pattern: 'ziggurat', name: 'Ziggurat' }),
  generateLevel('labyrinth', 9, { pattern: 'maze', name: 'Labyrinth' }),
  generateLevel('highlands', 12, { pattern: 'noise', name: 'Highlands' }),
  generateLevel('grand-labyrinth', 11, { pattern: 'maze', name: 'Grand Labyrinth' }),
  generateLevel('vast-plains', 21, { pattern: 'noise', name: 'Vast Plains', smoothness: 0.85 }),
];

export function getLevel(index = 0): Level {
  const wrapped = ((index % baseLevels.length) + baseLevels.length) % baseLevels.length;
  return cloneLevel(baseLevels[wrapped]);
}

export function cloneLevel(level: Level): Level {
  return {
    ...level,
    heights: [...level.heights],
  };
}

/** Stable id/name for the always-fresh "Random" level (see createRandomLevel). */
export const RANDOM_LEVEL_ID = 'random';
export const RANDOM_LEVEL_NAME = 'Random';

/**
 * Pure wrapper around `generateLevel` that produces a level tagged as
 * `randomized: true`, with a stable id/name regardless of the seed. The
 * caller supplies a fresh seed each time for genuine randomization.
 */
export function createRandomLevel(
  seed: number | string,
  size: number = defaultSize,
): Level {
  const level = generateLevel(seed, size);
  return {
    ...level,
    id: RANDOM_LEVEL_ID,
    name: RANDOM_LEVEL_NAME,
    randomized: true,
  };
}
